import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import * as Realm from 'realm-web';

/**
 * Hook für die zentrale Verwaltung der Authentifizierung
 * Stellt Funktionen für Token-Validierung und Token-Refresh bereit
 * 
 * @param {Function} setLoggedIn - Funktion zum Setzen des Login-Status
 * @returns {Object} Authentifizierungsfunktionen und -status
 */
export const useAuth = (setLoggedIn) => {
  const navigate = useNavigate();
  const token = localStorage.getItem('accessToken');
  const app = new Realm.App({ id: "data-ywwpoom" });
  const stayLoggedIn = localStorage.getItem('stayLoggedIn') === 'true';
  const [isAuthenticated, setIsAuthenticated] = useState(Boolean(token));
  
  /**
   * Validiert den übergebenen Token
   * 
   * @param {string} token - Der zu überprüfende Access-Token
   * @returns {Promise<boolean>} True, wenn der Token gültig ist, sonst false
   */
  const validateToken = (token) => {
    return new Promise((resolve, reject) => {
      if (!token) {
        resolve(false);
        return;
      }
      
      try {
        fetch(process.env.REACT_APP_EMPLOYEE_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Access-Control-Request-Headers': '*',
          },
          body: JSON.stringify({ token }),
          redirect: 'follow'
        })
          .then(response => response.json())
          .then(result => resolve(!result.error))
          .catch(error => reject(error));
      } catch (error) {
        reject(error);
      }
    });
  };
  
  /**
   * Aktualisiert den Access-Token
   * 
   * @returns {Promise<boolean>} True, wenn der Token erfolgreich aktualisiert wurde, sonst false
   */
  const refreshAccessToken = async () => {
    try {
      await app.currentUser.refreshAccessToken();
      const newAccessToken = app.currentUser.accessToken;
      localStorage.setItem('accessToken', newAccessToken);
      return true;
    } catch (error) {
      console.error('Token refresh error:', error);
      return false;
    }
  };
  
  /**
   * Verwaltet die Abmeldung des Benutzers
   */
  const handleLogout = () => {
    localStorage.removeItem('accessToken');
    setLoggedIn(false);
    navigate('/login');
  };
  
  /**
   * Überprüft den Token und aktualisiert ihn bei Bedarf
   */
  const validateAndRefreshToken = async () => {
    try {
      const isValid = await validateToken(token);
      
      if (!isValid) {
        if (stayLoggedIn) {
          const refreshed = await refreshAccessToken();
          if (!refreshed) {
            setIsAuthenticated(false);
            handleLogout();
          } else {
            const newToken = app.currentUser.accessToken;
            const isValidNewToken = await validateToken(newToken);
            if (!isValidNewToken) {
              setIsAuthenticated(false);
              handleLogout();
            } else {
              setIsAuthenticated(true);
              setLoggedIn(true);
            }
          }
        } else {
          setIsAuthenticated(false);
          handleLogout();
        }
      } else {
        setIsAuthenticated(true);
        setLoggedIn(true);
      }
    } catch (error) {
      console.error('Token validation error:', error);
      setIsAuthenticated(false);
      handleLogout();
    }
  };
  
  // Initiale Authentifizierungsprüfung
  useEffect(() => {
    if (!token) {
      handleLogout();
      return;
    }
    
    validateAndRefreshToken();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);
  
  return {
    isAuthenticated,
    token,
    app,
    stayLoggedIn,
    validateToken,
    refreshAccessToken,
    handleLogout,
    validateAndRefreshToken
  };
};
